/* global axios */
import ApiClient from './ApiClient';

class LeadsAPI extends ApiClient {
  constructor() {
    super('leads', { accountScoped: true });
  }

  getLeads(params = {}) {
    return axios.get(this.url, { params });
  }

  getAvailableInboxes() {
    return axios.get(`${this.url}/available_inboxes`);
  }

  getMyLeads(params = {}) {
    return axios.get(`${this.url}/my_leads`, { params });
  }

  // FILA-1: fila do PRÓPRIO consultor (deriva de Current.user no backend).
  // tab: 'not_contacted' (Novos Leads) | 'contacted' (Abordados).
  getMyQueue(
    tab = 'not_contacted',
    { period = 'today', startDate = null, endDate = null } = {}
  ) {
    return axios.get(`${this.url}/my_queue`, {
      params: {
        tab,
        period,
        ...(period === 'custom' && startDate ? { start_date: startDate } : {}),
        ...(period === 'custom' && endDate ? { end_date: endDate } : {}),
      },
    });
  }

  bulkAssign({ conversationIds, agentId }) {
    return axios.post(`${this.url}/bulk_assign`, {
      conversation_ids: conversationIds,
      agent_id: agentId,
    });
  }

  // Sem bypass da trava de "já em atendimento": o antigo override_attendance_guard
  // ("Entregar responsabilidade ao supervisor") foi removido do sistema inteiro no
  // incidente SJP/conta 11 (set/2026) — distribuir por cima de um atendimento aberto só
  // movia o beco sem saída de lugar. Destravar é encerrar o atendimento, nunca pular a trava.
  bulkRedistribute({ conversationIds, inboxId, agentId }) {
    return axios.post(`${this.url}/bulk_redistribute`, {
      conversation_ids: conversationIds,
      inbox_id: inboxId,
      agent_id: agentId,
    });
  }

  redistribute({ conversationId, targetInboxId, targetAgentId }) {
    return axios.post(`${this.url}/${conversationId}/redistribute`, {
      target_inbox_id: targetInboxId,
      target_agent_id: targetAgentId,
    });
  }

  getConsultantLeads(
    consultantId,
    tab = 'contacted',
    {
      period = 'today',
      startDate = null,
      endDate = null,
      consultantIds = [],
    } = {}
  ) {
    const consultantParams = consultantIds.length
      ? { consultant_ids: consultantIds.join(',') }
      : { consultant_id: consultantId };

    return axios.get(`${this.url}/consultant_leads`, {
      params: {
        ...consultantParams,
        tab,
        period,
        ...(period === 'custom' && startDate ? { start_date: startDate } : {}),
        ...(period === 'custom' && endDate ? { end_date: endDate } : {}),
      },
    });
  }

  getConsultantStageLeads(
    consultantId,
    stage,
    { period = 'today', startDate = null, endDate = null } = {}
  ) {
    return axios.get(`${this.url}/consultant_stage_leads`, {
      params: {
        consultant_id: consultantId,
        stage,
        period,
        ...(period === 'custom' && startDate ? { start_date: startDate } : {}),
        ...(period === 'custom' && endDate ? { end_date: endDate } : {}),
      },
    });
  }

  revertDistribution(conversationId) {
    return axios.post(`${this.url}/${conversationId}/revert`);
  }

  // FILA-2: gate ANTES de iniciar — verifica se o contato já está em atendimento
  // cross-franquia. Retorna { em_atendimento, detalhes:{consultor,franquia,desde} }.
  verifyStart(conversationId) {
    return axios.post(`${this.url}/${conversationId}/verify_start`);
  }

  // FILA-1 (D9): supervisão marca/desmarca um lead como abordado.
  markApproached(conversationId, approached) {
    return axios.post(`${this.url}/${conversationId}/mark_approached`, {
      approached,
    });
  }

  // D4/D5: encerra o atendimento de um contato/lead e remove o bloqueio.
  closeAttendance({ conversationDisplayId }) {
    return axios.post(`${this.url}/close_attendance`, {
      conversation_display_id: conversationDisplayId,
    });
  }

  // Lista TODOS os atendimentos abertos do contato do lead — tela de resolução alcançada
  // pelo link "Copiar link" do bloqueio. conversationDisplayId é o lead de REFERÊNCIA
  // (mesma resolução que verify_start já usa no backend).
  getAttendances(conversationDisplayId) {
    return axios.get(`${this.url}/${conversationDisplayId}/attendances`);
  }

  // Mesma tela de resolução, agora em LOTE: um bloqueio de distribuição costuma travar
  // vários leads de uma vez e abrir uma aba por lead é inviável. Recebe os display_ids de
  // REFERÊNCIA (os mesmos do `attendances` single) e devolve
  // `{ references, attendances, requested_count, truncated }`.
  getAttendancesBulk(conversationDisplayIds) {
    return axios.get(`${this.url}/attendances_bulk`, {
      params: {
        conversation_display_ids: (conversationDisplayIds || []).join(','),
      },
    });
  }

  getHistory() {
    return axios.get(`${this.url}/history`);
  }
}

export default new LeadsAPI();
